/*
Confidentialité — équivalent PWA du PRIVACY.md Android.

Le contenu factuel diffère du document Android sur les détails de
stockage (IndexedDB + Cache API au lieu de SQLite + filesystem) et
sur les caveats iOS (éviction possible quand le stockage de l'appareil
est saturé) — mais la promesse "tout reste local, rien ne part" est
identique. Surface ce texte explicitement satisfait l'obligation
d'information GDPR pour l'audience UE.
*/

import { mountInfoScreen, type InfoScreenContent } from './components/InfoScreen'

const kContent: InfoScreenContent = {
	title: 'Confidentialité',
	leadHtml: `
		CanoPaname est une application 100&nbsp;% locale. Tes captures, tes
		photos, ta progression, tes badges&nbsp;: tout reste sur ton appareil.
		Rien n'est jamais envoyé vers un serveur géré par l'app.
	`,
	sections: [
		{
			heading: '📦 Ce qui est stocké, et où',
			bodyHtml: `
				<ul class="info-list">
					<li>
						<strong>Captures</strong> (espèce, position, date) —
						base IndexedDB privée à l'origine de l'app.
					</li>
					<li>
						<strong>Photos</strong> — Blobs IndexedDB, recompressés
						en JPEG ~500&nbsp;KB et strippés de leur EXIF (GPS,
						numéro de série appareil, software) à la capture.
					</li>
					<li>
						<strong>Préférences</strong> — IndexedDB (onboarding vu,
						intro splash vue) + un drapeau localStorage par bannière
						UI (mise à jour SW, stockage persistant, install).
					</li>
					<li>
						<strong>Cache des assets</strong> — service worker (shell
						HTML/JS/CSS, dataset arbres, tuiles cartographiques).
						Effacé en désinstallant ou en supprimant les données
						du site dans les réglages du navigateur.
					</li>
				</ul>
			`,
		},
		{
			heading: '🚫 Ce qui n\'est jamais fait',
			bodyHtml: `
				<ul class="info-list">
					<li>Aucun envoi de tes captures, photos ou positions vers un serveur.</li>
					<li>Aucune télémétrie, aucun analytics, aucun cookie tiers.</li>
					<li>Aucun compte, aucune authentification.</li>
					<li>Aucun service de notifications push.</li>
					<li>
						Aucun service Google requis (ni Play Services, ni Maps, ni
						reCAPTCHA, ni Fonts).
					</li>
				</ul>
			`,
		},
		{
			heading: '🌐 Connexions réseau',
			bodyHtml: `
				<p>L'app effectue exactement deux types d'appels réseau&nbsp;:</p>
				<ul class="info-list">
					<li>
						<strong>Tuiles cartographiques</strong> —
						<code>tiles.openfreemap.org</code> (OpenStreetMap, sans
						identifiant). Chaque tuile est une requête HTTPS anonyme&nbsp;;
						le SW les met en cache pour une utilisation hors-ligne.
					</li>
					<li>
						<strong>Bundle statique</strong> — au premier lancement, le
						navigateur télécharge le HTML, le JavaScript, le dataset
						arbres et les fiches Wikipédia FR. Tout est ensuite
						accessible hors-ligne via le service worker.
					</li>
				</ul>
				<p class="info-note">
					Les liens externes sortants (Wikipédia, fiche essence Ville de
					Paris, etc.) que tu peux taper depuis une fiche-espèce ouvrent
					ton navigateur — ce trafic-là dépend du site visité, pas de
					CanoPaname.
				</p>
			`,
		},
		{
			heading: '🔐 Permissions du navigateur',
			bodyHtml: `
				<ul class="info-list">
					<li>
						<strong>Position fine (GPS)</strong> — mesurer la distance
						à un arbre (&lt;&nbsp;30&nbsp;m) pour autoriser la capture.
						La position n'est jamais transmise.
					</li>
					<li>
						<strong>Caméra</strong> — photographier l'arbre capturé. Le
						fichier reste local.
					</li>
					<li>
						<strong>Stockage persistant</strong> — demandé après ta
						première capture pour que le système ne supprime pas tes photos
						quand l'appareil est saturé. Tu peux refuser&nbsp;; dans ce
						cas un export ZIP régulier est recommandé.
					</li>
				</ul>
			`,
		},
		{
			heading: '💾 Sauvegarde et portabilité',
			bodyHtml: `
				<p>
					Tu peux exporter toutes tes captures en ZIP via
					<em>Profil → Exporter</em>. Le fichier ZIP est généré
					localement, téléchargé via le navigateur, et reste sur le
					stockage que tu choisis. Le ZIP n'est pas chiffré&nbsp;: si
					tu veux protéger cette sauvegarde, mets-la dans un cloud
					chiffré (Cryptomator, Tresorit, etc.).
				</p>
				<p>
					Le format ZIP est compatible avec l'app Android CanoPaname,
					dans les deux sens.
				</p>
			`,
		},
		{
			heading: '🇪🇺 RGPD',
			bodyHtml: `
				<p>
					CanoPaname ne collecte aucune donnée personnelle. En conséquence&nbsp;:
				</p>
				<ul class="info-list">
					<li>Pas de responsable de traitement à désigner.</li>
					<li>Pas de finalité de traitement à déclarer.</li>
					<li>
						Tes droits d'accès / rectification / effacement
						s'exercent localement&nbsp;: depuis les réglages du
						navigateur, supprimer les données du site efface
						intégralement ton historique.
					</li>
				</ul>
			`,
		},
		{
			heading: '✉️ Contact',
			bodyHtml: `
				<p>
					Question, bug, ou attribution oubliée&nbsp;? Ouvre une issue
					sur
					<a class="info-link" href="https://github.com/m4xim1nus/CanoPaname" target="_blank" rel="noopener">le repo GitHub</a>.
				</p>
			`,
		},
	],
	footerHtml: `
		<p class="info-footnote">
			Dernière mise à jour&nbsp;: 2026-05-10.
		</p>
	`,
}

export function mountPrivacyScreen(inRoot: HTMLElement): void
{
	mountInfoScreen(inRoot, kContent)
}
